import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { AppIcon, applyTheme, effectiveTheme, IconMoon, IconScan, IconSun } from "@wilco/ui";
import { Command } from "cmdk";
import { useState } from "react";
import { deskQuery } from "../desk/desk.ts";
import { useCan } from "../auth.ts";
import { t } from "../i18n.ts";
import { visibleSections } from "./sections.ts";
import { useGoTo } from "./targets.tsx";

/**
 * Палитра Ctrl+K: разделы, плитки стола со счётчиком, тема и «Знайти код»
 * для строки от двух символов тем же путём, что скан.
 */
export function Palette({
  open,
  onOpenChange,
  onScan,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onScan: (code: string) => void;
}) {
  const navigate = useNavigate();
  const go = useGoTo();
  const can = useCan();
  const { data: desk } = useQuery(deskQuery);
  const [query, setQuery] = useState("");
  const code = query.trim();

  const run = (action: () => void) => {
    onOpenChange(false);
    setQuery("");
    action();
  };

  const dark = effectiveTheme() === "dark";

  return (
    <Command.Dialog
      open={open}
      onOpenChange={onOpenChange}
      label={t("shell.palette")}
      overlayClassName="w-dialog-overlay"
      contentClassName="palette"
      aria-describedby={undefined}
    >
      <Command.Input className="palette-input" value={query} onValueChange={setQuery} placeholder={t("palette.placeholder")} />
      <Command.List className="palette-list">
        {/* С кодом пусто не бывает: «Знайти код» есть всегда. */}
        {code.length < 2 ? <Command.Empty className="palette-empty">{t("palette.empty")}</Command.Empty> : null}
        {code.length >= 2 ? (
          // Код показывается всегда, фильтр по буквам к нему не относится.
          <Command.Group heading={t("palette.group.actions")} className="palette-group" forceMount>
            <Command.Item value={`code ${code}`} className="palette-item" onSelect={() => run(() => onScan(code))} forceMount>
              <IconScan />
              {t("palette.findCode", { code })}
            </Command.Item>
          </Command.Group>
        ) : null}
        <Command.Group heading={t("palette.group.go")} className="palette-group">
          <Command.Item className="palette-item" onSelect={() => run(() => void navigate({ to: "/" }))}>
            <AppIcon name="overview" size={20} />
            {t("section.desk")}
          </Command.Item>
          {visibleSections(can).map((s) => (
            <Command.Item
              key={s.id}
              className="palette-item"
              onSelect={() => run(() => go(s.target))}
            >
              <AppIcon name={s.icon} size={20} />
              {t(s.label)}
            </Command.Item>
          ))}
        </Command.Group>
        {desk ? (
          <Command.Group heading={t("palette.group.tiles")} className="palette-group">
            {desk.groups
              .flatMap((g) => g.tiles)
              .filter((tile) => tile.count)
              .map((tile) => {
                const Icon = tile.icon;
                return (
                  <Command.Item
                    key={tile.key}
                    className="palette-item"
                    onSelect={() => run(() => go(tile.target))}
                  >
                    <Icon />
                    {t("palette.tile", { title: t(tile.title), count: tile.count ?? 0 })}
                  </Command.Item>
                );
              })}
          </Command.Group>
        ) : null}
        <Command.Group heading={t("palette.group.actions")} className="palette-group">
          <Command.Item className="palette-item" onSelect={() => run(() => applyTheme(dark ? "light" : "dark"))}>
            {dark ? <IconSun /> : <IconMoon />}
            {`${t("shell.theme")}: ${dark ? t("shell.theme.light") : t("shell.theme.dark")}`}
          </Command.Item>
          <Command.Item className="palette-item" onSelect={() => run(() => void navigate({ to: "/ui" }))}>
            <IconScan />
            {t("palette.ui")}
          </Command.Item>
        </Command.Group>
      </Command.List>
    </Command.Dialog>
  );
}
